import type {
  CompatibilityGroup,
  MixRule,
  MixRuleCap,
} from "@/lib/catalog/types";

const GROUP_LABELS_EN: Record<CompatibilityGroup, string> = {
  A: "Roses & Garden Roses",
  B: "Spray Roses",
  C: "Hydrangeas",
  D: "Specialty",
  E: "Greenery",
};

const GROUP_LABELS_ES: Record<CompatibilityGroup, string> = {
  A: "Rosas y Garden Roses",
  B: "Spray Roses",
  C: "Hortensias",
  D: "Especialidad",
  E: "Verdes",
};

export function groupLabel(
  group: CompatibilityGroup,
  locale: "en" | "es" = "en",
): string {
  return locale === "es" ? GROUP_LABELS_ES[group] : GROUP_LABELS_EN[group];
}

// #468 C1: canShareBox moved to lib/cart/box-rules.ts and is now mixGroup-based
// (Alexis's merged groups), not sub-category. The old subcategory canShareBox is
// retired - subCategoryOf below survives only for invoice display order + the
// mixGroup pre-seed, never for a live mixing decision.

// #430: detectMixTransition (+ the MixConflict modal) were removed. The
// Rose+Garden cross-mix is gone (#353/#429): standard and garden roses live in
// separate sub-category buckets that never share a box, so there is no mix
// transition to detect. The mixRule cap (applyMixCapIfNeeded) is likewise
// unreachable - a group-A box can never hold two categories.

// Returns the effective per-box capacity for a given box code under a mix
// rule. Used by cart capacity checks once a mix is in effect.
export function mixCap(
  rule: MixRule,
  boxCode: "QB" | "HB",
): MixRuleCap | null {
  return rule[boxCode] ?? null;
}

// DIRECTIVE #022: V3 sub-category. Group A splits into Roses (A-rose)
// vs Garden Roses (A-gardenrose) because the mix-rule cap (QB drops
// from 5 to 4 when crossed) would force worse packings; the auto-
// packer keeps them in separate sub-buckets. Groups B/C/D/E pass
// through unchanged.
export type SubCategory =
  | "A-rose"
  | "A-gardenrose"
  | "B"
  | "C"
  | "D"
  | "D-lily"
  | "E";

// #429: Asiatic + Oriental lilies (bought by bunch) box ONLY with each other -
// never with any other specialty. They live in group D (specialty) but are
// carved into their own "D-lily" sub-bucket. The two lily slugs share the
// bucket with each other; every other specialty stays in plain "D".
const LILY_SLUGS: ReadonlySet<string> = new Set([
  "asiatic-lily",
  "oriental-lily",
]);

export function subCategoryOf(variety: {
  compatibilityGroup: CompatibilityGroup;
  category: string;
  slug: string;
}): SubCategory {
  if (variety.compatibilityGroup === "A") {
    return variety.category === "garden-spray-rose" ? "A-gardenrose" : "A-rose";
  }
  if (variety.compatibilityGroup === "D" && LILY_SLUGS.has(variety.slug)) {
    return "D-lily";
  }
  return variety.compatibilityGroup;
}

